/**
 * Контекст для работы с типами сервисов.
 * Предоставляет единый источник данных о доступных модулях системы.
 */
import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef, ReactNode } from 'react';
import { serviceTypeService } from '../services/serviceType.service';
import type { ServiceType } from '../types/serviceType';

interface ServiceTypesContextType {
  /** Список доступных типов сервисов */
  serviceTypes: ServiceType[];
  /** Загрузка данных */
  isLoading: boolean;
  /** Ошибка загрузки */
  error: string | null;
  /** Данные загружены */
  loaded: boolean;
  /** Перезагрузить данные */
  refresh: () => Promise<void>;
  /** Получить тип по коду */
  getByCode: (code: string) => ServiceType | undefined;
  /** Получить название по коду */
  getLabel: (code: string) => string;
  /** Получить цвет по коду */
  getColor: (code: string) => string;
}

const ServiceTypesContext = createContext<ServiceTypesContextType | null>(null);

interface ServiceTypesProviderProps {
  children: ReactNode;
}

export function ServiceTypesProvider({ children }: ServiceTypesProviderProps) {
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  // Dedup guard — защищает от двойного fetch в React 18 strict mode
  // и при конкурентных вызовах refresh() (TODO #1 из plan-eng-review).
  const loadingRef = useRef(false);
  const loadedRef = useRef(false);
  const inFlightRef = useRef<Promise<void> | null>(null);

  const loadServiceTypes = useCallback(async (force = false) => {
    if (!force && loadedRef.current) return;
    if (inFlightRef.current) return inFlightRef.current; // дедуп — вернём тот же promise
    loadingRef.current = true;
    setIsLoading(true);
    setError(null);
    const promise = (async () => {
      try {
        const data = await serviceTypeService.getEnabled();
        setServiceTypes(data);
        setLoaded(true);
        loadedRef.current = true;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load service types';
        setError(message);
        console.error('[ServiceTypesContext] Error loading service types:', err);
      } finally {
        loadingRef.current = false;
        inFlightRef.current = null;
        setIsLoading(false);
      }
    })();
    inFlightRef.current = promise;
    return promise;
  }, []);

  const refresh = useCallback(() => loadServiceTypes(true), [loadServiceTypes]);

  // Загрузка при монтировании
  useEffect(() => {
    void loadServiceTypes();
  }, [loadServiceTypes]);

  const getByCode = useCallback(
    (code: string) => serviceTypes.find((st) => st.code === code),
    [serviceTypes]
  );

  const getLabel = useCallback(
    (code: string) => {
      const st = serviceTypes.find((s) => s.code === code);
      return st?.name || code;
    },
    [serviceTypes]
  );

  const getColor = useCallback(
    (code: string) => {
      const st = serviceTypes.find((s) => s.code === code);
      return st?.color || 'var(--gray-500)';
    },
    [serviceTypes]
  );

  const value = useMemo(
    () => ({
      serviceTypes,
      isLoading,
      error,
      loaded,
      refresh,
      getByCode,
      getLabel,
      getColor,
    }),
    [serviceTypes, isLoading, error, loaded, refresh, getByCode, getLabel, getColor]
  );

  return <ServiceTypesContext.Provider value={value}>{children}</ServiceTypesContext.Provider>;
}

/**
 * Хук для получения списка типов сервисов.
 */
export function useServiceTypes(): ServiceTypesContextType {
  const context = useContext(ServiceTypesContext);
  if (!context) {
    throw new Error('useServiceTypes must be used within ServiceTypesProvider');
  }
  return context;
}

/**
 * Хук для получения массива кодов доступных типов.
 */
export function useServiceTypeCodes(): string[] {
  const { serviceTypes } = useServiceTypes();
  return useMemo(() => serviceTypes.map((st) => st.code), [serviceTypes]);
}
